/** @jsx jsx */
import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { jsx } from '@emotion/core';
import {
  Step,
  StepConnector,
  StepLabel,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';

import { ICustomTheme } from 'interfaces';
import { StepIcon } from 'components';
import * as S from './styles';
import { isRewardingAutoSelector } from 'reducers/challenge.reducer';

const {
  cssContainer,
  cssMarginTop,
  cssStep,
  dashedConnectorStyles,
  stepLabelStyles,
} = S;

const useDashedConnectorStyles = makeStyles(dashedConnectorStyles);
const useStepLabelStyles = makeStyles(stepLabelStyles);

export type StepType = {
  id?: number;
  name: string;
  status?: 'start' | 'first' | 'done' | 'next' | 'disabled';
  icon?: JSX.Element;
  [key: string]: any;
};
type StepContentType = (props: {
  index: number;
  step: StepType;
  numberOfStep?: number;
}) => JSX.Element;

type StepperType = {
  AboveStepContent?: StepContentType;
  BelowStepContent?: StepContentType;
  /**
   * AboveStepContent and BelowStepContent are components which will be given the step and its index
   * */
  chidren?: JSX.Element;
  connector?: JSX.Element;
  containerStyle?: Record<string, any>;
  currentStep?: number;
  disabled?: boolean;
  setFirstStepRef?: (ref: HTMLDivElement) => void;
  iconSize?: 'large' | 'medium';
  noMobileConnector?: boolean;
  steps?: StepType[];
  stepStyleFn?: (index: number) => (theme: ICustomTheme) => Record<string, any>;
  /**
   * stepStyleFn is a function that is expected to receive index and return regular css style
   */
  [key: string]: any;
};

// /!\ Shared component between the ChallengeResults component and the GoalsAndConditions component (defi/deal/actionListe)(goal type = generic, except: custom or result)
// changing the style of one will change the style of the other,
const Stepper = ({
  AboveStepContent,
  BelowStepContent,
  children,
  connector,
  containerStyle = {},
  currentStep,
  disabled = false,
  setFirstStepRef,
  iconSize = 'medium',
  noMobileConnector = false,
  steps,
  stepStyleFn = () => () => ({}),
  ...otherProps
}: StepperType): JSX.Element => {
  const theme: ICustomTheme = useTheme();
  const dashedConnectorClasses = useDashedConnectorStyles();
  const stepLabelClasses = useStepLabelStyles();
  const isDownXs = useMediaQuery(theme.breakpoints.down('xs'));
  const { id: challengeId }: { id: string } = useParams();
  const isRewardingAuto = useSelector(
    isRewardingAutoSelector(Number(challengeId)),
  );

  const activeStep = currentStep !== undefined ? currentStep + 1 : undefined;

  return (
    <div css={[cssContainer, containerStyle]}>
      {children}
      <S.Stepper
        activeStep={activeStep}
        alternativeLabel
        nbOfSteps={steps?.length}
        {...otherProps}
        connector={
          isDownXs && noMobileConnector ? (
            <React.Fragment /> // remove connectors for mobile
          ) : (
            connector || (
              <StepConnector
                classes={dashedConnectorClasses}
                className={`${disabled ? 'disabled' : ''} ${
                  isRewardingAuto ? 'isRewardingAuto' : ''
                }`}
              />
            )
          )
        }
      >
        {steps?.map((step, index) => {
          return (
            <Step
              key={step.id || step.name}
              ref={(ref: HTMLDivElement) => {
                if (setFirstStepRef && index === 0) {
                  setFirstStepRef(ref);
                }
              }}
              css={{ ...cssStep(theme), ...stepStyleFn(index)(theme) }}
            >
              {AboveStepContent && (
                <AboveStepContent
                  index={index}
                  step={step}
                  numberOfStep={steps?.length}
                />
              )}

              <StepLabel
                classes={stepLabelClasses}
                StepIconComponent={() => (
                  <StepIcon
                    icon={step.icon}
                    status={step.status}
                    size={iconSize}
                  />
                )}
              />

              <div css={cssMarginTop}>
                {BelowStepContent ? (
                  <BelowStepContent index={index} step={step} />
                ) : (
                  <S.Text className={step.status}>{step.name}</S.Text>
                )}
              </div>
            </Step>
          );
        })}
      </S.Stepper>
    </div>
  );
};

export default Stepper;
